/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* global document */

const JSG = require('../../JSG');
const DataProvider = require('./DataProvider');
const Selection = require('./Selection');
const {RowHeaderModel, ColumnHeaderModel} = require('./HeaderModel');
const Cell = require('./Cell');
const Numbers = require('../../commons/Numbers');
const {NumberFormatter} = require('@cedalo/number-format');
const CellRange = require('./CellRange');
const HeaderSection = require('./HeaderSection');

let gid = 1;

const cellDefaults = {
	attributes: {
		key: false,
		level: 0,
		protected: true,
		visible: true,
		note: undefined,
	},
	formats: {
		fillstyle: 0,
		fillcolor: JSG.theme.fill,
		leftbordercolor: JSG.theme.border,
		leftborderstyle: 0,
		leftborderwidth: 1,
		topbordercolor: JSG.theme.border,
		topborderstyle: 0,
		topborderwidth: 1,
		rightbordercolor: JSG.theme.border,
		rightborderstyle : 0,
		rightborderwidth: 1,
		bottombordercolor: JSG.theme.border,
		bottomborderstyle: 0,
		bottomborderwidth: 1
	},
	textFormats: {
		valign: 2,
		halign: 3,
		fontsize: 9,
		fontname: 'Verdana',
		fontcolor: JSG.theme.text,
		fontstyle: 0,
		numberformat: 'General',
		localculture: 'general',
	}
};


const applyFormat = (target, source) => {
	if (!source) {
		return;
	}
	if (source.fillstyle !== undefined) {
		target.fillstyle = source.fillstyle === null ? cellDefaults.fillstyle : source.fillstyle;
	}
	if (source.fillcolor !== undefined) {
		target.fillcolor = source.fillcolor === null ? cellDefaults.fillcolor : source.fillcolor;
	}
	if (source.leftborderstyle !== undefined) {
		target.leftborderstyle = source.leftborderstyle === null ? cellDefaults.leftborderstyle : source.leftborderstyle;
	}
	if (source.leftborderwidth !== undefined) {
		target.leftborderwidth = source.leftborderwidth === null ? cellDefaults.leftborderwidth : source.leftborderwidth;
	}
	if (source.leftbordercolor !== undefined) {
		target.leftbordercolor = source.leftbordercolor === null ? cellDefaults.leftbordercolor : source.leftbordercolor;
	}
	if (source.rightborderstyle !== undefined) {
		target.rightborderstyle = source.rightborderstyle === null ? cellDefaults.rightborderstyle : source.rightborderstyle;
	}
	if (source.rightborderwidth !== undefined) {
		target.rightborderwidth = source.rightborderwidth === null ? cellDefaults.rightborderwidth : source.rightborderwidth;
	}
	if (source.rightbordercolor !== undefined) {
		target.rightbordercolor = source.rightbordercolor === null ? cellDefaults.rightbordercolor : source.rightbordercolor;
	}
	if (source.topborderstyle !== undefined) {
		target.topborderstyle = source.topborderstyle === null ? cellDefaults.topborderstyle : source.topborderstyle;
	}
	if (source.topborderwidth !== undefined) {
		target.topborderwidth = source.topborderwidth === null ? cellDefaults.topborderwidth : source.topborderwidth;
	}
	if (source.topbordercolor !== undefined) {
		target.topbordercolor = source.topbordercolor === null ? cellDefaults.topbordercolor : source.topbordercolor;
	}
	if (source.bottomborderstyle !== undefined) {
		target.bottomborderstyle = source.bottomborderstyle === null ? cellDefaults.bottomborderstyle : source.bottomborderstyle;
	}
	if (source.bottomborderwidth !== undefined) {
		target.bottomborderwidth = source.bottomborderwidth === null ? cellDefaults.bottomborderwidth : source.bottomborderwidth;
	}
	if (source.bottombordercolor !== undefined) {
		target.bottombordercolor = source.bottombordercolor === null ? cellDefaults.bottombordercolor : source.bottombordercolor;
	}
};

const applyTextFormat = (target, source) => {
	if (!source) {
		return;
	}
	if (source.valign !== undefined) {
		target.valign = source.valign === null ? cellDefaults.valign : source.valign;
	}
	if (source.halign !== undefined) {
		target.halign = source.halign === null ? cellDefaults.halign : source.halign;
	}
	if (source.fontsize !== undefined) {
		target.fontsize = source.fontsize === null ? cellDefaults.fontsize : source.fontsize;
	}
	if (source.fontname !== undefined) {
		target.fontname = source.fontname === null ? cellDefaults.fontname : source.fontname;
	}
	if (source.fontcolor !== undefined) {
		target.fontcolor = source.fontcolor === null ? cellDefaults.fontcolor : source.fontcolor;
	}
	if (source.fontstyle !== undefined) {
		target.fontstyle = source.fontstyle === null ? cellDefaults.fontstyle : source.fontstyle;
	}
	if (source.numberformat !== undefined) {
		target.numberformat = source.numberformat === null ? cellDefaults.numberformat : source.numberformat;
	}
	if (source.localculture !== undefined) {
		target.localculture = source.localculture === null ? cellDefaults.localculture : source.localculture;
	}
};

const applyAttributes = (target, source) => {
	if (!source) {
		return;
	}
	if (source.key !== undefined) {
		target.key = source.key === null ? cellDefaults.key : source.key;
	}
	if (source.level !== undefined) {
		target.level = source.level === null ? cellDefaults.level : source.level;
	}
	if (source.protected !== undefined) {
		target.protected = source.protected === null ? cellDefaults.protected : source.protected;
	}
	if (source.visible !== undefined) {
		target.visible = source.visible === null ? cellDefaults.visible : source.visible;
	}
	if (source.note !== undefined) {
		target.note = source.note === null ? cellDefaults.note : source.note;
	}
};

module.exports = class SheetModel {
	constructor(id, name) {
		this._id = id === undefined ? gid : id;
		this._name = name === undefined ? `Sheet${gid}` : name;
		this._cells = new DataProvider(this);
		this._rows = new RowHeaderModel();
		this._columns = new ColumnHeaderModel();
		this._shapes = {
			changed: 0,
			shapes: [],
			timestamp: 0,
			version: 1,
		};

		this._protected = false;
		this._greyIfRows = true;
		this._showFormulas = false;
		this._showGrid = true;
		this._showHeader = true;
		this._colOffset = -2;

		this._selection = new Selection(this);
		this._selection.selectCell(this, new JSG.Point(2, 0));

		// create default cell for default format
		this.resetDefaultFormat();

		gid += 1;
	}

	static findSheetModel(name) {
		return JSG.findSheetModel(name);
	}

	copy() {
		const copy = new SheetModel(this.id, this.name);
		copy._cells = this.cells;
		copy._rows = this.rows;
		copy._coluns = this.columns;
		copy._greyIfRows = this._greyIfRows;
		copy._showFormulas = this._showFormulas;
		copy._showGrid = this._showGrid;
		copy._showHeader = this._showHeader;
		copy._protected = this._protected;

		return copy;
	}

	get id() {
		return this._id;
	}

	set id(id) {
		this._id = id;
	}

	get name() {
		return this._name;
	}

	set name(name) {
		this._name = name;
	}

	get shapes() {
		return this._shapes;
	}

	set shapes(shapes) {
		this._shapes = shapes;
	}

	get cells() {
		return this._cells;
	}

	set cells(cells) {
		this._cells = cells;
	}

	get colOffset() {
		return this._colOffset;
	}

	get rows() {
		return this._rows;
	}

	set rows(rows) {
		this._rows = rows;
	}

	get columns() {
		return this._columns;
	}

	set columns(columns) {
		this._columns = columns;
	}

	get selection() {
		return this._selection;
	}

	set selection(selection) {
		this._selection = selection;
	}

	get maxColumns() {
		return this._columns.size;
	}

	get maxRows() {
		return this._rows.size;
	}

	set showGrid(flag) {
		this._showGrid = flag;
	}

	get showGrid() {
		return this._showGrid;
	}

	set showHeader(flag) {
		this._showHeader = flag;
	}

	get showHeader() {
		return this._showHeader;
	}

	set showFormulas(flag) {
		this._showFormulas = flag;
	}

	get showFormulas() {
		return this._showFormulas;
	}

	set greyIfRows(flag) {
		this._greyIfRows = flag;
	}

	get greyIfRows() {
		return this._greyIfRows;
	}

	set protected(flag) {
		this._protected = flag;
	}

	get protected() {
		return this._protected;
	}

	resetDefaultFormat() {
		this._defaultCell = this.createDefaultCell();
	}

	get defaultCell() {
		return this._defaultCell;
	}

	createDefaultCell() {
		const cell = new Cell();
		cell.properties = {};

		return cell;
	}

	getFormatProperties(column, row, defaults = false) {
		// performance -> consider to assign defined properties to cell once, when received from server
		const result = {};

		if (defaults) {
			Object.assign(result, cellDefaults.formats);
		}

		applyFormat(result, this.defaultCell.format);
		applyFormat(result, this.columns.getSectionFormat(column));
		applyFormat(result, this.rows.getSectionFormat(row));

		const cell = this.cells.getRC(column, row);
		if (cell !== undefined) {
			applyFormat(result, cell.format);
		}

		return result;
	}

	getTextFormatProperties(column, row, defaults = false) {
		const result = {};

		if (defaults) {
			Object.assign(result, cellDefaults.textFormats);
		}

		applyTextFormat(result, this.defaultCell.textFormat);
		applyTextFormat(result, this.columns.getSectionTextFormat(column));
		applyTextFormat(result, this.rows.getSectionTextFormat(row));

		const cell = this.cells.getRC(column, row);
		if (cell !== undefined) {
			applyTextFormat(result, cell.textFormat);
		}

		return result;
	}

	getCellProperties(column, row, defaults = false) {
		const result = {};

		if (defaults) {
			Object.assign(result, cellDefaults.attributes);
		}

		applyAttributes(result, this.defaultCell.attributes);
		applyAttributes(result, this.columns.getSectionAttributes(column));
		applyAttributes(result, this.rows.getSectionAttributes(row));

		const cell = this.cells.getRC(column, row);
		if (cell !== undefined) {
			applyAttributes(result, cell.attributes);
		}

		return result;
	}

	getFormattedValue(expr, value, textFormat, showFormulas) {
		let result = {
			value,
			formattedValue: value,
			color: undefined,
			type: 'general'
		};

		if (showFormulas) {
			if (expr.hasFormula()) {
				result.value = expr.toLocaleString(JSG.getParserLocaleSettings(), {
					sheet: this,
					useName: true
				});
			} else if (result.value === undefined) {
				result.value = '#NV';
			}
			result.formattedValue = result.value;
		} else if (Numbers.isNumber(result.value) && result.value !== undefined && textFormat !== undefined) {
			const numberFormat = textFormat.numberformat;
			if (numberFormat !== undefined) {
				const fmt = numberFormat || '';
				const set = textFormat.localculture || '';
				const type = set.split(';');
				try {
					result = NumberFormatter.formatNumber(fmt, result.value, type[0]);
				} catch (e) {
					result.formattedValue = '#####';
				}
				result.value = value;
				[result.type] = type;
				if (result.formattedValue === '' && (result.type === 'date' || result.type === 'time')) {
					result.formattedValue = `#INVALID_${result.type.toUpperCase()}`;
					result.value = result.formattedValue;
				}
			}
		}

		if (typeof result.value === 'boolean') {
			result.formattedValue = result.value.toString().toUpperCase();
		}

		return result;
	}

	readFromClipboard(reader) {
		const root = reader.getRoot();
		const selection = reader.getObject(root, 'selection');
		if (selection === undefined) {
			return undefined;
		}

		const range = reader.getAttribute(selection, 'range');
		if (range === undefined) {
			return undefined;
		}

		const sheetName = reader.getAttribute(selection, 'name');
		// use this sheet, if sheet can not be found -> sheet in another machine
		const sourceSheet = SheetModel.findSheetModel(sheetName) || this;

		const result = {
			cut: reader.getAttribute(selection, 'cut') === '1',
			rangeString: range,
			range: CellRange.parse(range, sourceSheet, true, true).shiftFromSheet(),
			data: new DataProvider(this),
			columns: [],
			rows: [],
			defaultCell: this.defaultCell.copy()
		};

		let object;
		let c;
		let r;
		let cellObject;

		reader.iterateObjects(selection, (name, child) => {
			switch (name) {
			case 'defaultcell':
				cellObject = reader.getObject(child, 'cell');
				result.defaultCell.read(reader, cellObject);
				break;
			case 'columns':
				reader.iterateObjects(child, (subname, subchild) => {
					c = Number(reader.getAttribute(subchild, 'index')) - result.range.getX1();
					object = new HeaderSection();
					object.read(reader, subchild);
					object._index = c;
					object._title = undefined;
					result.columns[c] = object;
				});
				break;
			case 'rows':
				reader.iterateObjects(child, (subname, subchild) => {
					r = Number(reader.getAttribute(subchild, 'index')) - result.range.getY1();
					object = new HeaderSection();
					object.read(reader, subchild);
					object._index = r;
					object._title = undefined;
					result.rows[r] = object;
				});
				break;
			case 'cells':
				reader.iterateObjects(child, (subname, subchild) => {
					r = Number(reader.getAttribute(subchild, 'r')) - result.range.getY1();
					c = Number(reader.getAttribute(subchild, 'c')) - result.range.getX1();
					object = new Cell();
					cellObject = reader.getObject(subchild, 'cell');
					object.read(reader, cellObject);
					result.data.setRCTo(c, r, object);
				});
				break;
			}
		});

		return result;
	}
};
